import React, { useState, useContext } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { 
  Menu, 
  X, 
  Search, 
  Book, 
  User, 
  LogOut, 
  Home, 
  Settings, 
  FileText, 
  Users, 
  Shield,
  HelpCircle,
  MessageCircle,
  Crown 
} from 'lucide-react';
import AuthContext from '../contexts/AuthContext';
import ThemeContext from '../contexts/ThemeContext';
import { THEMES } from '../utils/theme';
import SearchBar from '../components/SearchBar';
import ThemeMenu from '../components/ThemeMenu';
import MeritDisplay from '../components/MeritDisplay';
import NotificationDropdown from '../components/notifications/NotificationDropdown';

/**
 * Main layout component for authenticated pages
 * Includes navigation, header, and content area
 */
const MainLayout = () => {
  const { user, logout, updateUser } = useContext(AuthContext);
  const { theme, setTheme } = useContext(ThemeContext);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const navigate = useNavigate();
  const [themeOpen, setThemeOpen] = useState(false);

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const toggleSearch = () => {
    setSearchOpen(!searchOpen);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Theme toggle handler
  const handleThemeToggle = () => {
    // Cycle through themes, but skip DEFAULT and CUSTOM
    const themes = [THEMES.DARK, THEMES.LIGHT, THEMES.BLUE, THEMES.GREEN, THEMES.PURPLE];
    const currentIndex = themes.indexOf(theme);
    const nextIndex = (currentIndex + 1) % themes.length;
    setTheme(themes[nextIndex]);
  };

  return (
    <div className="flex h-screen bg-primary text-primary">
      {/* Sidebar */}
      <div 
        className={`fixed inset-y-0 left-0 z-50 w-64 bg-black text-white transform ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        } transition-transform duration-300 ease-in-out lg:translate-x-0 lg:static lg:w-64`}
      >
        {/* Sidebar header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-700">
        <div className="flex items-center">
          <div className="relative">
            <div className="flex items-baseline">
              <span className="text-2xl font-extrabold tracking-tighter bg-gradient-to-r from-red-700 to-red-500 text-transparent bg-clip-text">EDX</span>
              <span className="ml-1 text-xl font-bold inline-block relative transform -translate-y-0.5">
                <span className="text-white">Red</span>
                <div className="absolute bottom-0 left-0 right-0 -mr-3 h-0.5 bg-gradient-to-r from-red-600 to-transparent"></div>
              </span>
            </div>
          </div>
        </div>
          <button 
            className="p-1 rounded-md lg:hidden focus:outline-none focus:ring-2 focus:ring-gray-600"
            onClick={toggleSidebar}
          >
            <X size={24} />
          </button>
        </div>
        
        {/* Sidebar content */}
        <nav className="mt-5 px-2">
            <NavLink 
              to="/school" 
              className={({ isActive }) => 
                `flex items-center px-4 py-2 mt-2 text-sm rounded-lg ${
                  isActive 
                    ? 'bg-gray-800 text-red-400' 
                    : 'text-gray-300 hover:bg-gray-700'
                }`
              }
              onClick={() => setSidebarOpen(false)}
            >
              <Home className="mr-3" size={20} />
              <span>Home</span>
            </NavLink>
            
            <NavLink 
              to="/courses" 
              className={({ isActive }) => 
                `flex items-center px-4 py-2 mt-2 text-sm rounded-lg ${
                  isActive 
                    ? 'bg-gray-800 text-red-400' 
                    : 'text-gray-300 hover:bg-gray-700'
                }`
              }
              onClick={() => setSidebarOpen(false)}
            >
              <Book className="mr-3" size={20} />
              <span>Courses</span>
            </NavLink>
            
            <NavLink 
              to="/professors" 
              className={({ isActive }) => 
                `flex items-center px-4 py-2 mt-2 text-sm rounded-lg ${
                  isActive 
                    ? 'bg-gray-800 text-red-400' 
                    : 'text-gray-300 hover:bg-gray-700'
                }`
              }
              onClick={() => setSidebarOpen(false)}
            >
              <Users className="mr-3" size={20} />
              <span>Professors</span>
            </NavLink>
            
            <NavLink 
              to="/my-courses" 
              className={({ isActive }) => 
                `flex items-center px-4 py-2 mt-2 text-sm rounded-lg ${
                  isActive 
                    ? 'bg-gray-800 text-red-400' 
                    : 'text-gray-300 hover:bg-gray-700'
                }`
              }
              onClick={() => setSidebarOpen(false)}
            >
              <Book className="mr-3" size={20} />
              <span>My Courses</span>
            </NavLink>
            
            <NavLink 
              to="/my-materials" 
              className={({ isActive }) => 
                `flex items-center px-4 py-2 mt-2 text-sm rounded-lg ${
                  isActive 
                    ? 'bg-gray-800 text-red-400' 
                    : 'text-gray-300 hover:bg-gray-700'
                }`
              }
              onClick={() => setSidebarOpen(false)}
            >
              <FileText className="mr-3" size={20} />
              <span>My Materials</span>
            </NavLink>
            
            <NavLink 
              to="/chat" 
              className={({ isActive }) => 
                `flex items-center px-4 py-2 mt-2 text-sm rounded-lg ${
                  isActive 
                    ? 'bg-gray-800 text-red-400' 
                    : 'text-gray-300 hover:bg-gray-700'
                }`
              }
              onClick={() => setSidebarOpen(false)}
            >
              <MessageCircle className="mr-3" size={20} />
              <span>Lexi (AI)</span>
            </NavLink>
            
            <NavLink 
              to="/subscription" 
              className={({ isActive }) => 
                `flex items-center px-4 py-2 mt-2 text-sm rounded-lg ${
                  isActive 
                    ? 'bg-gray-800 text-red-400' 
                    : 'text-gray-300 hover:bg-gray-700'
                }`
              }
              onClick={() => setSidebarOpen(false)}
            >
              <Crown className="mr-3" size={20} />
              <span>Subscription</span>
            </NavLink>
            
            <div className="border-t border-gray-700 my-4"></div>
            
            <NavLink 
              to="/profile" 
              className={({ isActive }) => 
                `flex items-center px-4 py-2 mt-2 text-sm rounded-lg ${
                  isActive 
                    ? 'bg-gray-800 text-red-400' 
                    : 'text-gray-300 hover:bg-gray-700'
                }`
              }
              onClick={() => setSidebarOpen(false)}
            >
              <User className="mr-3" size={20} />
              <span>Profile</span>
            </NavLink>

            <NavLink 
              to={`/users/${user?.id}`} 
              className={({ isActive }) => 
                `flex items-center px-4 py-2 mt-2 text-sm rounded-lg ${
                  isActive 
                    ? 'bg-gray-800 text-red-400' 
                    : 'text-gray-300 hover:bg-gray-700'
                }`
              }
              onClick={() => setSidebarOpen(false)}
            >
              <Users className="mr-3" size={20} /> {/* Or use another appropriate icon */}
              <span>Public Profile</span>
            </NavLink>
            
            <NavLink 
              to="/settings" 
              className={({ isActive }) => 
                `flex items-center px-4 py-2 mt-2 text-sm rounded-lg ${
                  isActive 
                    ? 'bg-gray-800 text-red-400' 
                    : 'text-gray-300 hover:bg-gray-700'
                }`
              }
              onClick={() => setSidebarOpen(false)}
            >
              <Settings className="mr-3" size={20} />
              <span>Settings</span>
            </NavLink>

            <NavLink 
              to="/help" 
              className={({ isActive }) => 
                `flex items-center px-4 py-2 mt-2 text-sm rounded-lg ${
                  isActive 
                    ? 'bg-gray-800 text-red-400' 
                    : 'text-gray-300 hover:bg-gray-700'
                }`
              }
              onClick={() => setSidebarOpen(false)}
            >
              <HelpCircle className="mr-3" size={20} />
              <span>Help</span>
            </NavLink>
            
            {/* Admin Dashboard Link */}
            {user?.role === 'admin' && (
              <NavLink 
                to="/admin" 
                className={({ isActive }) => 
                  `flex items-center px-4 py-2 mt-2 text-sm rounded-lg ${
                    isActive 
                      ? 'bg-gray-800 text-red-400' 
                      : 'text-gray-300 hover:bg-gray-700'
                  }`
                }
                onClick={() => setSidebarOpen(false)}
              >
                <Shield className="mr-3" size={20} />
                <span>Admin Dashboard</span>
              </NavLink>
            )}
            
            {/* Moderator Dashboard Link */}
            {user?.role === 'moderator' && (
              <NavLink 
                to="/moderator" 
                className={({ isActive }) => 
                  `flex items-center px-4 py-2 mt-2 text-sm rounded-lg ${
                    isActive 
                      ? 'bg-gray-800 text-blue-400' 
                      : 'text-gray-300 hover:bg-gray-700'
                  }`
                }
                onClick={() => setSidebarOpen(false)}
              >
                <Shield className="mr-3" size={20} />
                <span>Moderator Panel</span>
              </NavLink>
            )}
            
            <button 
              onClick={handleLogout}
              className="flex w-full items-center px-4 py-2 mt-2 text-sm text-gray-300 rounded-lg hover:bg-gray-700"
            >
              <LogOut className="mr-3" size={20} />
              <span>Logout</span>
            </button>
        </nav>
        
        {/* Alpha Version Disclaimer */}
        <div className="absolute bottom-0 left-0 right-0 p-4">
          <div className="bg-red-700 text-white rounded-lg p-3 text-xs shadow-md">
            <div className="font-bold text-center mb-1">ALPHA VERSION</div>
            <p>This is an early version of the platform. Features may not work perfectly and may change without notice.</p>
          </div>
        </div>
      </div>
      
      {/* Main content */}
      <div className="flex flex-col flex-1 overflow-hidden">
        {/* Header */}
        <header className="bg-white dark:bg-gray-800 shadow-sm z-20">
          <div className="px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              <div className="flex items-center">
                <button
                  onClick={toggleSidebar}
                  className="p-2 rounded-md lg:hidden focus:outline-none focus:ring-2 focus:ring-gray-600"
                >
                  <Menu size={24} />
                </button>
                <div className="ml-4 lg:ml-0">
                  {user?.school?.name && (
                    <div className="text-lg font-semibold">{user.school.name}</div>
                  )}
                </div>
              </div>
              
              <div className="flex items-center space-x-4">
                <MeritDisplay />
                <NotificationDropdown />
                <button
                  onClick={toggleSearch}
                  className="p-2 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-600"
                >
                  <Search size={20} />
                </button>
                
                <div className="relative">
                  <button
                    onClick={() => setThemeOpen(!themeOpen)}
                    className="p-2 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-600"
                  >
                    {theme === THEMES.DARK ? <span>☀️</span> : <span>🎨</span>}
                  </button>
                  
                  <ThemeMenu
                    isOpen={themeOpen}
                    onClose={() => setThemeOpen(false)}
                    theme={theme}
                    setTheme={setTheme}
                    updateUser={updateUser}
                    THEMES={THEMES}
                  />
                </div>
                
                <div className="relative">
                  <div className="flex items-center">
                    <div className="ml-3">
                      <div className="text-sm font-medium">
                        {user?.name}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Search bar */}
          {searchOpen && (
            <div className="p-4 border-t border-gray-200 dark:border-gray-700">
              <SearchBar onClose={() => setSearchOpen(false)} />
            </div>
          )}
        </header>
        
        {/* Page content */}
        <main className="flex-1 overflow-y-auto bg-primary text-primary p-4 sm:p-6 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default MainLayout;